// src/repositories/supabase/SupabaseJournalEntryRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { IJournalEntryRepository, DBJournalEntry } from '../types';
import { requireAuth } from '../../services/supabaseClient';

/** Exported for reuse by BackupService.ts (raw exportBackup query — no repo method covers "all rows, all periods"). */
export function mapRowToDBJournalEntry(row: Record<string, unknown>): DBJournalEntry {
  // lines is stored as JSONB — Supabase auto-parses it to an array
  const lines = Array.isArray(row.lines) ? row.lines : [];
  return {
    dbId:       (row.db_id as number | null) ?? undefined,
    id:         row.id as string,
    firmId:     row.firm_id as string,
    companyId:  row.company_id as string,
    companyRuc: (row.company_ruc as string | null) ?? undefined,
    fecha:      row.fecha as string,
    glosa:      row.glosa as string,
    lines,
    totalDebe:  row.total_debe as string,
    totalHaber: row.total_haber as string,
    periodo:    row.periodo as string,
    tipo:       row.tipo as 'automatico' | 'manual',
    validated:  row.validated as boolean,
    annulled:   (row.annulled as boolean | null) ?? false,
    isDeleted:  (row.is_deleted as boolean | null) ?? false,
    source:     (row.source as 'auto' | 'manual' | null) ?? 'auto',
    createdBy:  (row.created_by as string | null) ?? undefined,
    createdAt:  (row.created_at as string | null) ?? undefined,
    posted:     (row.posted as boolean | null) ?? false,
    posted_at:  (row.posted_at as string | null) ?? null,
    posted_by:  (row.posted_by as string | null) ?? null,
    cuoPeriodo:     (row.cuo_periodo    as string | null) ?? undefined,
    transaction_id: (row.transaction_id as string | null) ?? undefined,
  };
}

export class SupabaseJournalEntryRepository implements IJournalEntryRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async getByPeriod(firmId: string, companyId: string, periodo: string): Promise<DBJournalEntry[]> {
    await requireAuth();
    // Excluir is_deleted=true y annulled=true: son fuente única para SyncContext
    // (Dashboard, EmprendedorDashboard, CajaYBancosPle, etc.) y para PleGenerator.
    // Sin estos filtros, los reportes mostrarían entries borrados/anulados con
    // estado '1' y SUNAT cruzaría inconsistencias contra PDT 621.
    const { data, error } = await this.client
      .from('journal_entries')
      .select(
        'db_id, id, firm_id, company_id, periodo, fecha, glosa, lines, ' +
        'validated, source, total_debe, total_haber, company_ruc, annulled, is_deleted, ' +
        'posted, posted_at, posted_by, cuo_periodo, transaction_id'
      )
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('periodo', periodo)
      .eq('posted', true)
      .neq('is_deleted', true)
      .neq('annulled', true);
    if (error) throw new Error(`[getByPeriod] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBJournalEntry);
  }

  async getByPeriodYTD(firmId: string, companyId: string, periodo: string): Promise<DBJournalEntry[]> {
    await requireAuth();
    const year = periodo.split('-')[0];
    const { data, error } = await this.client
      .from('journal_entries')
      .select(
        'db_id, id, firm_id, company_id, periodo, fecha, glosa, lines, ' +
        'validated, source, total_debe, total_haber, company_ruc, annulled, is_deleted, ' +
        'posted, posted_at, posted_by, cuo_periodo, transaction_id'
      )
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('posted', true)
      .neq('is_deleted', true)
      .neq('annulled', true)
      .gte('periodo', `${year}-01`)
      .lte('periodo', periodo)
      .order('fecha', { ascending: true });
    if (error) throw new Error(`[getByPeriodYTD] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBJournalEntry);
  }

  async getCumulative(firmId: string, companyId: string, periodo: string): Promise<DBJournalEntry[]> {
    await requireAuth();
    const { data, error } = await this.client
      .from('journal_entries')
      .select(
        'db_id, id, firm_id, company_id, periodo, fecha, glosa, lines, ' +
        'validated, source, total_debe, total_haber, company_ruc, annulled, is_deleted, ' +
        'posted, posted_at, posted_by, cuo_periodo, transaction_id'
      )
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('posted', true)
      .neq('is_deleted', true)
      .neq('annulled', true)
      .lte('periodo', periodo)
      .order('fecha', { ascending: true });
    if (error) throw new Error(`[getCumulative] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBJournalEntry);
  }

  async countByPeriod(firmId: string, companyId: string, periodo: string): Promise<number> {
    await requireAuth();
    const { count, error } = await this.client
      .from('journal_entries')
      .select('*', { count: 'exact', head: true })
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('periodo', periodo)
      .eq('posted', true)
      .neq('is_deleted', true)
      .neq('annulled', true);
    if (error) throw new Error(`[countByPeriod] ${error.message}`);
    return count ?? 0;
  }

  async countUnpostedByPeriod(firmId: string, companyId: string, periodo: string): Promise<number> {
    await requireAuth();
    const { count, error } = await this.client
      .from('journal_entries')
      .select('*', { count: 'exact', head: true })
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('periodo', periodo)
      .eq('posted', false)
      .eq('is_deleted', false);
    if (error) throw new Error(`[countUnpostedByPeriod] ${error.message}`);
    return count ?? 0;
  }
}
